import { createSlice } from '@reduxjs/toolkit'
import { flow, map } from 'lodash/fp'
import { pickBy } from 'lodash'

import { filterInvalidOptionsForCategory } from './utils'
import api, { createWrappedAsyncThunk } from 'src/api'
import { fetchUser } from 'src/modules/auth/slice'

export const initialState = {
  category: '',
  subcategory: '',
  allergenFilterChoices: [],
  allergenFilters: [],
  categoryChoices: [],
  dietaryFilterChoices: [],
  dietaryFilters: [],
  sustainabilityFilterChoices: [],
  sustainabilityFilters: [],
  search: '',
  nearEnvironment: null,
  nearPostcode: '',
  product: {},
  supplier: {},
  suppliers: {
    hasMore: false,
    page: 0,
    section: '',
    results: [],
    total: 0,
    lastListScrollOffset: 0,
  },
}

const SLICE_NAME = 'buyer/suppliers'

// This allows us to change the URL prefix if a user is not logged in, so
// that we can query the public endpoints for supplier and products
const getUrlRoot = ({ getState }) => {
  const { user } = getState().auth
  if (user && user.organisation.type === 'buyer') {
    return `/buyer`
  } else {
    return ''
  }
}

// Fetching data
export const fetchAllergenFilterChoices = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchAllergenFilterChoices`,
  () => api.get('/allergens/')
)

export const fetchCategoryChoices = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchCategoryChoices`,
  () => api.get('/categories/')
)

export const fetchDietaryFilterChoices = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchDietaryFilterChoices`,
  () => api.get('/dietary/')
)

export const fetchSustainabilityFilterChoices = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchSustainabilityFilterChoices`,
  () => api.get('/sustainability/')
)

export const fetchSuppliers = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchSuppliers`,
  async (arg, thunkApi) => {
    const { section, ...params } = arg
    const cleanParams = pickBy(
      params,
      v => !!v && (!Array.isArray(v) || v.length)
    )
    let endpoint = `${getUrlRoot(thunkApi)}/suppliers/`
    if (section) {
      endpoint = `${endpoint}${section}/`
    }
    return await api.get(endpoint, {
      queryStringParameters: cleanParams,
    })
  }
)

export const fetchSingleSupplier = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchSingleSupplier`,
  (arg, thunkApi) => api.get(`${getUrlRoot(thunkApi)}/suppliers/${arg}/`)
)

export const fetchProduct = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchProduct`,
  (arg, thunkApi) => api.get(`${getUrlRoot(thunkApi)}/products/${arg}/`)
)
// Supplier actions
export const starSupplier = createWrappedAsyncThunk(
  `${SLICE_NAME}/starSupplier`,
  arg => api.post(`/buyer/suppliers/${arg}/star/`)
)

export const unstarSupplier = createWrappedAsyncThunk(
  `${SLICE_NAME}/unstarSupplier`,
  arg => api.post(`/buyer/suppliers/${arg}/unstar/`)
)

export const requestProposal = createWrappedAsyncThunk(
  `${SLICE_NAME}/requestProposal`,
  arg => api.post(`/buyer/suppliers/${arg}/request_proposal/`)
)

export const leaveReview = createWrappedAsyncThunk(
  `${SLICE_NAME}/leaveReview`,
  body => api.post(`/buyer/reviews/`, { body })
)

export const approveAgreement = createWrappedAsyncThunk(
  `${SLICE_NAME}/approveAgreement`,
  id => api.post(`/buyer/agreements/${id}/approve/`)
)

export const unapproveAgreement = createWrappedAsyncThunk(
  `${SLICE_NAME}/unapproveAgreement`,
  id => api.post(`/buyer/agreements/${id}/unapprove/`)
)

export const updateEnvironmentProducts = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateEnvironmentProducts`,
  arg => {
    const { environmentId, ...body } = arg
    return api.put(`/buyer/environments/${environmentId}/update_products/`, {
      body,
    })
  }
)

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    // Category
    setCategory(state, { payload: category }) {
      state.category = category

      // When setting a new category, make sure we remove and sustainability
      // filters that do not apply to this category
      const { sustainabilityFilters, sustainabilityFilterChoices } = state
      const validFilterIds = flow(
        map(id => sustainabilityFilterChoices.find(c => c.id === id)),
        choices => filterInvalidOptionsForCategory(category, choices),
        map(c => c.id)
      )(sustainabilityFilters)
      state.sustainabilityFilters = validFilterIds

      return state
    },
    setSubcategory(state, { payload }) {
      state.subcategory = payload
      return state
    },
    // Filters
    setAllergenFilters(state, { payload }) {
      state.allergenFilters = payload
      return state
    },
    setDietaryFilters(state, { payload }) {
      state.dietaryFilters = payload
      return state
    },
    setSustainabilityFilters(state, { payload }) {
      state.sustainabilityFilters = payload
      return state
    },
    // Suppliers list
    setSuppliers(state, { payload }) {
      state.suppliers = payload
      return state
    },
    // Search
    setSearch(state, { payload }) {
      state.search = payload
      return state
    },
    // Near environment
    setNearEnvironment(state, { payload }) {
      state.nearEnvironment = payload
      return state
    },
    setNearPostcode(state, { payload }) {
      state.nearPostcode = payload
      return state
    },
    setLastListScrollOffset(state, { payload }) {
      state.suppliers.lastListScrollOffset = payload
      return state
    },
  },
  extraReducers: {
    // Near environment
    [fetchUser.fulfilled]: (state, { payload }) => {
      if (payload.organisation.type === 'buyer') {
        // check nearEnvironment stored is valid, ie could be bad one
        // from localStorage
        const invalidEnvironment = payload.environments.every(
          p => p.id !== state.nearEnvironment
        )
        if (invalidEnvironment) {
          state.nearEnvironment = null
        }
      }
    },
    // Filters
    [fetchAllergenFilterChoices.fulfilled]: (state, { payload }) => {
      state.allergenFilterChoices = payload
      return state
    },
    [fetchCategoryChoices.fulfilled]: (state, { payload }) => {
      state.categoryChoices = payload
      return state
    },
    [fetchDietaryFilterChoices.fulfilled]: (state, { payload }) => {
      state.dietaryFilterChoices = payload
      return state
    },
    [fetchSustainabilityFilterChoices.fulfilled]: (state, { payload }) => {
      state.sustainabilityFilterChoices = payload
      return state
    },
    // Fetch list
    [fetchSuppliers.fulfilled]: (state, { meta, payload }) => {
      const { page, section } = meta.arg
      const { count, next, results } = payload
      state.suppliers.section = section
      state.suppliers.page = page
      state.suppliers.total = count
      state.suppliers.hasMore = Boolean(next)
      if (page === 1) {
        state.suppliers.results = results
      } else {
        state.suppliers.results = state.suppliers.results.concat(results)
      }
      return state
    },
    [fetchSingleSupplier.fulfilled]: (state, { payload }) => {
      state.supplier = payload
      return state
    },
    [fetchProduct.fulfilled]: (state, { payload }) => {
      state.product = payload
      return state
    },
    // Supplier actions
    [starSupplier.fulfilled]: (state, { meta, payload }) =>
      updateSupplier(state, meta.arg, s => (s.starred = true)),
    [unstarSupplier.fulfilled]: (state, { meta, payload }) =>
      updateSupplier(state, meta.arg, s => (s.starred = false)),
    [requestProposal.fulfilled]: (state, { meta, payload }) =>
      updateSupplier(state, meta.arg, s => (s.proposalStatus = 'requested')),
    [leaveReview.fulfilled]: (state, { payload: review }) => {
      if (state.supplier.id === review.supplier.id) {
        state.supplier.reviews = [review, ...state.supplier.reviews]
      }
      return state
    },
    [approveAgreement.fulfilled]: (state, { payload }) => {
      if (state.supplier.agreement.id === payload.id) {
        state.supplier.agreement = payload
      }
      return state
    },
    [unapproveAgreement.fulfilled]: (state, { payload }) => {
      if (state.supplier.agreement.id === payload.id) {
        state.supplier.agreement = payload
      }
      return state
    },
    [updateEnvironmentProducts.fulfilled]: (state, { meta, payload }) => {
      const { agreementId, environmentId, productIds } = meta.arg
      if (state.supplier.agreement.id === agreementId) {
        // Update all the products on the agreement to ensure this environment
        // is in them
        const { agreementProducts } = state.supplier.agreement
        agreementProducts.forEach(ap => {
          const productId = ap.product.id
          const shouldBeInEnv = productIds.includes(productId)
          const isInEnv = ap.environments.includes(environmentId)
          if (shouldBeInEnv && !isInEnv) {
            ap.environments = [...ap.environments, environmentId]
          } else if (!shouldBeInEnv && isInEnv) {
            ap.environments = ap.environments.filter(e => e !== environmentId)
          }
        })
      }
      return state
    },
  },
})

// Update supplier in list if present and also if in supplier profile
const updateSupplier = (state, id, update) => {
  const supplier = state.suppliers.results.find(s => s.id === id)
  if (supplier) {
    update(supplier)
  }
  if (state.supplier.id === id) {
    update(state.supplier)
  }
  return state
}

export const {
  setAllergenFilters,
  setCategory,
  setDietaryFilters,
  setPurchasingEnvironment,
  setSearch,
  setSubcategory,
  setSuppliers,
  setSustainabilityFilters,
  setNearEnvironment,
  setNearPostcode,
  setLastListScrollOffset,
} = slice.actions

export const actions = slice.actions

export default slice.reducer
